import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import './App.css';
import './styles/Loading.css';

// Components
import GradeSelector from './components/GradeSelector';
import SubjectPanel from './components/SubjectPanel';
import ChapterPanel from './components/ChapterPanel';
import NotesQuestionsPanel from './components/NotesQuestionsPanel';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import ExamManagement from './components/ExamManagement';
import Report from './components/Report';

// Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Navigation Component
const NavigationButtons: React.FC = () => {
  return (
    <>
      <Link to="/exams">
        <button className="exams-btn">Exams</button>
      </Link>
      <Link to="/report">
        <button className="report-btn">Report</button>
      </Link>
    </>
  );
};

// Main Dashboard Component
const Dashboard: React.FC = () => {
  const [selectedGradeId, setSelectedGradeId] = React.useState<number | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = React.useState<number | null>(null);
  const [selectedChapterId, setSelectedChapterId] = React.useState<number | null>(null);
  const { user, logout } = useAuth();

  const handleGradeSelect = (gradeId: number) => {
    setSelectedGradeId(gradeId);
    setSelectedSubjectId(null);
    setSelectedChapterId(null);
  };

  const handleSubjectSelect = (subjectId: number) => {
    setSelectedSubjectId(subjectId);
    setSelectedChapterId(null);
  };

  const handleChapterSelect = (chapterId: number) => {
    setSelectedChapterId(chapterId);
  };

  const handleLogout = () => {
    logout();
  };

  // Determine the name to display based on the user object structure
  const getDisplayName = () => {
    if (!user) return 'User';
    
    // Try both camelCase and snake_case formats
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else {
      // Fall back to username if names aren't available
      return user.username || 'User';
    }
  };

  return (
    <div className="container">
      <header>
        <h1>Notes Management</h1>
        <div className="user-info">
          <span>Welcome, {getDisplayName()}</span>
          <NavigationButtons />
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <main>
        <GradeSelector onSelectGrade={handleGradeSelect} />
        
        {selectedGradeId && (
          <SubjectPanel 
            gradeId={selectedGradeId} 
            onSelectSubject={handleSubjectSelect} 
          />
        )}
        
        {selectedSubjectId && (
          <ChapterPanel 
            subjectId={selectedSubjectId} 
            onSelectChapter={handleChapterSelect} 
          />
        )}
        
        {selectedChapterId && (
          <NotesQuestionsPanel chapterId={selectedChapterId} />
        )}
      </main>

      <footer>
        <p>&copy; 2025 Educational Content Management System</p>
      </footer>
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/exams" 
            element={
              <ProtectedRoute>
                <ExamManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/report" 
            element={
              <ProtectedRoute>
                <Report />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/" 
            element={<Navigate to="/dashboard" replace />} 
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;